import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, Switch, Button, Card, Space, Divider, Alert, message, Tag } from 'antd';
import { targetsAPI, agentsAPI } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const TargetForm = ({ form, initialValues, onFinish, loading }) => {
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const targetType = Form.useWatch('target_type', form);
  const selectedAgents = Form.useWatch('agent_ids', form);

  // Safety: ensure agents is always an array
  const safeAgents = Array.isArray(agents) ? agents : [];
  const targetTypes = [
    { value: 'ICMP', label: 'ICMP Ping' },
    { value: 'TCP', label: 'TCP Port' },
    { value: 'SSH', label: 'SSH' },
    // { value: 'HTTP', label: 'HTTP' },
    // { value: 'HTTPS', label: 'HTTP(S)' },
    { value: 'POSTGRESQL', label: 'PostgreSQL' },
    { value: 'MYSQL', label: 'MySQL/MariaDB' },
    // { value: 'MSSQL', label: 'SQL Server' },
    // { value: 'REDIS', label: 'Redis' },
    // { value: 'MONGODB', label: 'MongoDB' },
    // { value: 'SQLITE', label: 'SQLite' },
    { value: 'APP', label: 'Custom App' },
    { value: 'KAFKA', label: 'Kafka' },
    { value: 'MQTT', label: 'MQTT' },
    { value: 'RABBITMQ', label: 'RabbitMQ' },
    { value: 'WS', label: 'WebSocket (WS)' },
    { value: 'WSS', label: 'WebSocket Secure (WSS)' },
  ];

  const getDefaultPort = (type) => {
    const ports = {
      'POSTGRESQL': 5432,
      'MYSQL': 3306,
      'MSSQL': 1433,
      'REDIS': 6379,
      'MONGODB': 27017,
      'RABBITMQ': 5672,
      'KAFKA': 9092,
      'MQTT': 1883,
      'WS': 80,
      'WSS': 443
    };
    return ports[type] || null;
  };

  const mergedInitialValues = React.useMemo(() => {
    const defaults = {
      interval: 30,
      is_active: true,
      email_notification: true,
      webhook_url: '',
      agent_ids: [],
      options: {
        method: 'GET',
        expectedCode: 200,
        path: '/', // Default path for WS/WSS
      },
    };

    const base = initialValues || {};

    const mergedOptions = {
      ...defaults.options,
      ...(base.options || {}),
    };

    if (!mergedOptions.url && base.host) {
      mergedOptions.url = base.host;
    }

    // Handle agent_ids: convert comma-separated string to array for editing
    let agentIds = defaults.agent_ids;
    if (base.agent_id) {
      if (typeof base.agent_id === 'string' && base.agent_id.length > 0) {
        agentIds = base.agent_id.split(',').map(id => id.trim()).filter(id => id.length > 0);
      } else if (Array.isArray(base.agent_id)) {
        agentIds = base.agent_id;
      }
    }

    const merged = {
      ...defaults,
      ...base,
      agent_ids: agentIds,
      options: mergedOptions,
    };

    if (merged.port == null) {
      merged.port = getDefaultPort(base.target_type) || (base.target_type === 'SSH' ? 22 : undefined);
    }

    return merged;
  }, [initialValues]);

  useEffect(() => {
    // Set form values when mergedInitialValues changes
    // This happens when switching between edit and add modes
    // Do NOT reset fields here as Dashboard already sets values before modal opens
    form.setFieldsValue(mergedInitialValues);
  }, [mergedInitialValues, form]);

  // Fetch agents for delegation selection
  useEffect(() => {
    const fetchAgents = async () => {
      setLoadingAgents(true);
      try {
        const response = await agentsAPI.getAll();
        // API returns { success: true, data: { agents: [...], stats: {...} } }
        let agentsData = [];
        if (response && response.data && Array.isArray(response.data.agents)) {
          agentsData = response.data.agents;
        } else if (Array.isArray(response)) {
          agentsData = response;
        } else if (response && Array.isArray(response.data)) {
          agentsData = response.data;
        }
        setAgents(agentsData);
      } catch (error) {
        console.error('Error fetching agents:', error);
      } finally {
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  const renderTargetTypeFields = () => {
    switch (targetType) {
      case 'ICMP':
        return null;

      case 'TCP':
        return (
          <Form.Item
            name="port"
            label="Port"
            rules={[{ required: true, message: 'Please input port!' }]}
          >
            <InputNumber
              min={1}
              max={65535}
              style={{ width: '100%' }}
              placeholder={targetType === 'SSH' ? '22' : '80'}
            />
          </Form.Item>
        );

      case 'SSH':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label="Authentication"
              required
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name={['options', 'username']}
                  rules={[{ required: true, message: 'Username required!' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="Username" />
                </Form.Item>
                <Form.Item
                  name={['options', 'password']}
                  rules={[{ required: true, message: 'Password required!' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input.Password placeholder="Password" />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item
              name={['options', 'privateKey']}
              label="Private Key (Optional)"
            >
              <TextArea rows={4} placeholder="Paste your private key here..." />
            </Form.Item>
          </>
        );

      case 'HTTP':
      case 'HTTPS':
        return (
          <>
            <Form.Item
              name={['options', 'url']}
              label="URL"
              rules={[{ required: true, message: 'Please input URL!' }]}
            >
              <Input placeholder="example.com" />
            </Form.Item>
            <Form.Item
              name={['options', 'expectedCode']}
              label="Expected HTTP Status"
            >
              <Select placeholder="Select status code">
                <Option value={200}>200 OK</Option>
                <Option value={301}>301 Redirect</Option>
                <Option value={302}>302 Redirect</Option>
                <Option value={404}>404 Not Found</Option>
                <Option value={500}>500 Server Error</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name={['options', 'expectLogin']}
              label="Expect Login Page"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name={['options', 'customResponse']}
              label="Custom Response Check"
            >
              <TextArea placeholder="Expected response text" />
            </Form.Item>
            <Form.Item
              name={['options', 'headers']}
              label="Custom Headers (JSON)"
            >
              <TextArea rows={3} placeholder='{"Authorization": "Bearer token"}' />
            </Form.Item>
          </>
        );

      case 'POSTGRESQL':
      case 'MYSQL':
      case 'MSSQL':
      case 'REDIS':
      case 'MONGODB':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label="Authentication"
              required
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name={['options', 'username']}
                  rules={[{ required: true, message: 'Username required!' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="Username" />
                </Form.Item>
                <Form.Item
                  name={['options', 'password']}
                  rules={[{ required: true, message: 'Password required!' }]}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input.Password placeholder="Password" />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            {(targetType === 'POSTGRESQL' || targetType === 'MYSQL' || targetType === 'MSSQL' || targetType === 'MONGODB') && (
              <Form.Item
                name={['options', 'database']}
                label="Database Name"
                rules={[{ required: true, message: 'Database name required!' }]}
              >
                <Input placeholder="mydb" />
              </Form.Item>
            )}
            {targetType === 'MSSQL' && (
              <>
                <Form.Item
                  name={['options', 'encrypt']}
                  label="Encrypt Connection"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  name={['options', 'trustServerCertificate']}
                  label="Trust Server Certificate"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}
          </>
        );

      case 'SQLITE':
        return (
          <Form.Item
            name={['options', 'database']}
            label="Database File Path"
            rules={[{ required: true, message: 'Please input database file path!' }]}
          >
            <Input placeholder="/path/to/database.sqlite" />
          </Form.Item>
        );

      case 'APP':
        return (
          <>
            <Form.Item
              name={['options', 'url']}
              label="Application URL"
              rules={[{ required: true, message: 'Please input URL!' }]}
            >
              <Input placeholder="http://example.com/api/health" />
            </Form.Item>
            <Form.Item
              name={['options', 'method']}
              label="HTTP Method"
            >
              <Select>
                <Option value="GET">GET</Option>
                <Option value="POST">POST</Option>
                <Option value="PUT">PUT</Option>
                <Option value="DELETE">DELETE</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name={['options', 'expectedCode']}
              label="Expected Status Code"
            >
              <InputNumber min={100} max={599} />
            </Form.Item>
            <Form.Item
              name={['options', 'headers']}
              label="Headers (JSON)"
            >
              <TextArea rows={3} placeholder='{"Content-Type": "application/json"}' />
            </Form.Item>
            <Form.Item
              name={['options', 'body']}
              label="Request Body (JSON)"
            >
              <TextArea rows={4} placeholder='{"key": "value"}' />
            </Form.Item>
          </>
        );

      case 'KAFKA':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name={['options', 'topic']}
              label="Topic (Optional)"
              help="Check if specific topic exists"
            >
              <Input placeholder="my-topic" />
            </Form.Item>
          </>
        );

      case 'MQTT':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name={['options', 'topic']}
              label="Topic (Optional)"
              help="Check if can subscribe to topic"
            >
              <Input placeholder="sensors/temperature" />
            </Form.Item>
            <Form.Item
              name={['options', 'username']}
              label="Username (Optional)"
            >
              <Input placeholder="mqtt_user" />
            </Form.Item>
            <Form.Item
              name={['options', 'password']}
              label="Password (Optional)"
            >
              <Input.Password placeholder="mqtt_password" />
            </Form.Item>
          </>
        );

      case 'RABBITMQ':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label="Check Type"
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name={['options', 'queue']}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="Queue name" />
                </Form.Item>
                <Form.Item
                  name={['options', 'topic']}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="Exchange name" />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item
              label="Authentication (Optional)"
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name={['options', 'username']}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input placeholder="guest" />
                </Form.Item>
                <Form.Item
                  name={['options', 'password']}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input.Password placeholder="guest" />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Alert
              message="Note: Fill either Queue name or Exchange name, not both"
              type="info"
              showIcon
            />
          </>
        );

      case 'WS':
      case 'WSS':
        return (
          <>
            <Form.Item
              name="port"
              label="Port"
              rules={[{ required: true, message: 'Please input port!' }]}
            >
              <InputNumber
                min={1}
                max={65535}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name={['options', 'path']}
              label="WebSocket Path"
            >
              <Input placeholder="/ws or /socket.io" />
            </Form.Item>
            <Form.Item
              name={['options', 'testMessage']}
              label="Test Message (Optional)"
              help="Send a test message and wait for response"
            >
              <TextArea rows={3} placeholder='{"action":"ping"} or plain text message' />
            </Form.Item>
            <Form.Item
              name={['options', 'expectedResponse']}
              label="Expected Response (Optional)"
              help="Check if response contains this text"
            >
              <Input placeholder='pong or {"status":"ok"}' />
            </Form.Item>
            <Form.Item
              name={['options', 'headers']}
              label="Custom Headers (JSON, Optional)"
            >
              <TextArea rows={3} placeholder='{"Authorization": "Bearer token"}' />
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  // getDefaultPort moved earlier to centralize defaults

  const handleTargetTypeChange = (value) => {
    // setTargetType(value);
    // Clear port field when target type changes
    form.setFieldsValue({
      port: getDefaultPort(value),
    });
  };

  const handleTestWebhook = async () => {
    const webhookUrl = form.getFieldValue('webhook_url');
    if (!webhookUrl) {
      message.warning('Please enter a webhook URL first');
      return;
    }

    setTestingWebhook(true);
    try {
      await targetsAPI.testWebhook(webhookUrl);
      message.success('Webhook test successful! Check your webhook endpoint for the test payload.');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to test webhook';
      message.error(`Webhook test failed: ${errorMsg}`);
    } finally {
      setTestingWebhook(false);
    }
  };



  return (
    <Card title="Target Configuration" style={{ marginTop: 16 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={mergedInitialValues}
      >
        <Form.Item
          name="name"
          label="Target Name"
          rules={[{ required: true, message: 'Please input target name!' }]}
        >
          <Input placeholder="My Target" />
        </Form.Item>

        <Form.Item
          name="host"
          label="Host"
          rules={[{ required: true, message: 'Please input host!' }]}
        >
          <Input placeholder="example.com or 192.168.1.1" />
        </Form.Item>

        <Form.Item
          name="target_type"
          label="Check Type"
          rules={[{ required: true, message: 'Please select check type!' }]}
        >
          <Select
            placeholder="Select check type"
            onChange={handleTargetTypeChange}
          >
            {targetTypes.map(type => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {renderTargetTypeFields()}

        <Divider />

        <Form.Item
          name="interval"
          label="Check Interval (seconds)"
          rules={[{ required: true, message: 'Please input interval!' }]}
        >
          <InputNumber
            min={5}
            style={{ width: '100%' }}
            placeholder="30"
          />
        </Form.Item>

        <Divider orientation="left">Agent Delegation (Optional)</Divider>

        <Alert
          message="Delegate monitoring to agents instead of central server"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="agent_ids"
          label="Assign to Agents"
          help="Select one or more agents to monitor this target. Leave empty for central monitoring."
        >
          <Select
            mode="multiple"
            placeholder="Select agents (optional)"
            loading={loadingAgents}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.props.children[1].props.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {safeAgents.map(agent => (
              <Option key={agent.agent_id} value={agent.agent_id}>
                <Space>
                  <Tag color={agent.online ? 'green' : 'default'}>
                    {agent.online ? 'Online' : 'Offline'}
                  </Tag>
                  <span>{agent.agent_name}</span>
                  <Tag color="blue">{agent.agent_id.substring(0, 8)}...</Tag>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedAgents && selectedAgents.length > 0 && (
          <Alert
            message={`Monitoring will be delegated to ${selectedAgents.length} agent(s). Central server will not run checks.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider orientation="left">Notifications</Divider>

        <Form.Item
          name="email_notification"
          label="Email Notification"
          valuePropName="checked"
          help="Send email when target status changes (DOWN/TIMEOUT/UP)"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="webhook_url"
          label="Webhook URL"
          help={
            <span>
              Send POST request to this URL when target status changes.
              Use <code>/webhook</code> for Webhook Receiver or <code>/webhook/test</code> for testing.
            </span>
          }
        >
          <Space.Compact style={{ width: '100%' }}>
            <Input placeholder="http://localhost:4002/webhook" />
            <Button
              type="default"
              onClick={handleTestWebhook}
              loading={testingWebhook}
            >
              Test
            </Button>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          name="is_active"
          label="Active"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            {initialValues ? 'Update Target' : 'Create Target'}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TargetForm;
